import { useEffect, useMemo, useState } from 'react';
import { Clock3, X } from 'lucide-react';

const COOLDOWN_KEY = 'mirror_rate_limit_cooldown';

function storedCooldown() {
  try {
    const value = JSON.parse(sessionStorage.getItem(COOLDOWN_KEY) || 'null');
    if (!value) return null;
    if (value.code === 'monthly_ai_budget_reached' || Number(value.retryAt) > Date.now()) return value;
    sessionStorage.removeItem(COOLDOWN_KEY);
  } catch {
    try { sessionStorage.removeItem(COOLDOWN_KEY); } catch { /* Storage can be unavailable in hardened browsing modes. */ }
  }
  return null;
}

export function formatCountdown(totalSeconds) {
  const seconds = Math.max(0, Math.ceil(Number(totalSeconds) || 0));
  if (seconds >= 86_400) {
    const days = Math.floor(seconds / 86_400);
    const hours = Math.floor((seconds % 86_400) / 3_600);
    return `${days}d ${String(hours).padStart(2, '0')}h`;
  }
  if (seconds >= 3_600) {
    const hours = Math.floor(seconds / 3_600);
    const minutes = Math.floor((seconds % 3_600) / 60);
    const rest = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
  }
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

export function isCooldownFor(cooldownState, operation) {
  const { cooldown, remainingSeconds } = cooldownState || {};
  if (!cooldown || remainingSeconds <= 0) return false;
  if (cooldown.code === 'monthly_ai_budget_reached' || cooldown.source === 'openai') return operation.startsWith('ai_');
  if (cooldown.scope === 'ai_render') return operation === 'ai_render';
  if (cooldown.scope === 'ai_fill') return operation === 'ai_scan';
  return operation === 'sync' && cooldown.source === 'mirror';
}

export function useRateLimitCooldown() {
  const [cooldown, setCooldown] = useState(storedCooldown);
  const [now, setNow] = useState(Date.now());
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    const handler = event => {
      const next = event.detail || null;
      setCooldown(next);
      if (next) {
        try { sessionStorage.setItem(COOLDOWN_KEY, JSON.stringify(next)); } catch { /* The live timer still works in memory. */ }
      }
      setNow(Date.now());
      setAnnouncement(next?.code === 'monthly_ai_budget_reached'
        ? 'Monthly MIЯЯOЯ Threads used.'
        : `${next?.source === 'openai' ? 'AI provider' : 'MIЯЯOЯ'} cooldown started.`);
    };
    window.addEventListener('mirror:cooldown', handler);
    return () => window.removeEventListener('mirror:cooldown', handler);
  }, []);

  useEffect(() => {
    if (!cooldown?.retryAt) return undefined;
    const tick = () => setNow(Date.now());
    tick();
    const timer = window.setInterval(tick, cooldown.code === 'monthly_ai_budget_reached' ? 60_000 : 1_000);
    return () => window.clearInterval(timer);
  }, [cooldown?.retryAt]);

  const remainingSeconds = cooldown?.retryAt
    ? Math.max(0, Math.ceil((cooldown.retryAt - now) / 1000))
    : 0;

  useEffect(() => {
    if (!cooldown?.retryAt || remainingSeconds > 0) return undefined;
    setAnnouncement('Cooldown complete. You can try again.');
    const timer = window.setTimeout(() => {
      try { sessionStorage.removeItem(COOLDOWN_KEY); } catch { /* Ignore storage cleanup failures. */ }
      setCooldown(null);
    }, 4500);
    return () => window.clearTimeout(timer);
  }, [cooldown?.retryAt, remainingSeconds]);

  return useMemo(() => ({
    cooldown,
    remainingSeconds,
    ready: !!cooldown?.retryAt && remainingSeconds === 0,
    announcement,
    clear: () => {
      try { sessionStorage.removeItem(COOLDOWN_KEY); } catch { /* Ignore storage cleanup failures. */ }
      setCooldown(null);
    },
  }), [announcement, cooldown, remainingSeconds]);
}

function noticeCopy(cooldown, ready) {
  if (ready) return { title: 'Ready', message: 'The cooldown is complete. You can try again.' };
  if (cooldown.code === 'monthly_ai_budget_reached') {
    const reset = cooldown.resetAt ? new Date(cooldown.resetAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric' }) : 'next month';
    return { title: 'Monthly MIЯЯOЯ Threads', message: `Threads refresh ${reset}. Organizing and private uploads still work.` };
  }
  if (cooldown.source === 'openai') {
    return { title: 'AI provider cooldown', message: 'The AI provider is busy. Uploads and closet edits still work.' };
  }
  if (cooldown.scope === 'auth') {
    return { title: 'Sign-in cooldown', message: 'MIЯЯOЯ is protecting this profile after repeated sign-in attempts.' };
  }
  if (cooldown.scope === 'ai_render') {
    return { title: 'Try-On cooldown', message: 'MIЯЯOЯ is pacing higher-cost try-ons to protect usage.' };
  }
  if (cooldown.scope === 'ai_fill') {
    return { title: 'AI scan cooldown', message: 'MIЯЯOЯ is pacing scans to protect usage.' };
  }
  return { title: 'Private sync cooldown', message: 'MIЯЯOЯ is finishing your private photo batch. Nothing was lost.' };
}

export function RateLimitNotice({ state }) {
  const { cooldown, remainingSeconds, ready, announcement, clear } = state;
  if (!cooldown) return null;
  const copy = noticeCopy(cooldown, ready);
  const monthly = cooldown.code === 'monthly_ai_budget_reached';
  return <aside className={`rate-limit-notice ${cooldown.source || 'mirror'}`} aria-label={copy.title}>
    <Clock3 aria-hidden="true" />
    <div><strong>{copy.title}</strong><p>{copy.message}</p></div>
    {!monthly ? <time role="timer" aria-live="off" aria-label={`${remainingSeconds} seconds remaining`}>{ready ? 'READY' : formatCountdown(remainingSeconds)}</time> : null}
    <button onClick={clear} aria-label="Dismiss cooldown notice"><X /></button>
    <span className="sr-only" role="status" aria-live="polite">{announcement}</span>
  </aside>;
}
